/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PAYMENT MANAGEMENT SERVICE
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Unified service to manage both Manual Pay and Auto-Renew Subscription
 * Controls site access and navigation button visibility
 * 
 * @author MyTamilKart Development Team
 * @version 1.0
 * @created 2025-12-19
 */

import api from '../api/axiosInstance';
import planService from './planService';

/**
 * Payment Type Constants
 */
export const PAYMENT_TYPE = {
  NONE: 'none',
  MANUAL_PAY: 'manual_pay',
  AUTO_RENEW: 'auto_renew',
  FREE_TRIAL: 'free_trial'
};

/**
 * Payment Status Constants
 */
export const PAYMENT_STATUS = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
  NONE: 'none'
};

class PaymentManagementService {
  /**
   * Get comprehensive payment information for a user
   * Checks both manual pay and auto-renew subscription
   * 
   * @param {string} businessUserId - The business user ID
   * @returns {Promise<Object>} Payment information object
   */
  async getPaymentInfo(businessUserId) {
    try {
      console.log('[PaymentManagement] Fetching payment info for:', businessUserId);

      // Fetch both manual plan and subscription info in parallel
      const [manualPlanResponse, subscriptionResponse] = await Promise.all([
        this.getManualPlanInfo(businessUserId),
        this.getSubscriptionInfo(businessUserId)
      ]);

      // Determine payment type and status
      const paymentInfo = await this.determinePaymentType(manualPlanResponse, subscriptionResponse, businessUserId);

      console.log('[PaymentManagement] Payment info determined:', paymentInfo);
      return paymentInfo;
    } catch (error) {
      console.error('[PaymentManagement] Error fetching payment info:', error);
      return {
        paymentType: PAYMENT_TYPE.NONE,
        status: PAYMENT_STATUS.NONE,
        hasAccess: false,
        showManualPayButton: true,
        showSubscriptionButton: false,
        message: 'Error fetching payment information'
      };
    }
  }

  /**
   * Get manual plan information
   * 
   * @param {string} businessUserId - The business user ID
   * @returns {Promise<Object>} Manual plan info
   */
  async getManualPlanInfo(businessUserId) {
    try {
      const response = await api.get('/business/plan/info', {
        params: { business_user_id: businessUserId }
      });

      if (response.data.status === 'success' && response.data.result) {
        return {
          hasManualPlan: true,
          ...response.data.result
        };
      }

      return { hasManualPlan: false };
    } catch (error) {
      console.error('[PaymentManagement] Error fetching manual plan:', error);
      return { hasManualPlan: false };
    }
  }

  /**
   * Get subscription information
   * 
   * @param {string} businessUserId - The business user ID
   * @returns {Promise<Object>} Subscription info
   */
  async getSubscriptionInfo(businessUserId) {
    try {
      const response = await api.get('/subscription/info', {
        params: { business_user_id: businessUserId }
      });

      if (response.data.status === 'success' && response.data.result) {
        const result = response.data.result;
        
        // If has subscription, sync status with Razorpay
        if (result.has_subscription && result.subscription_id) {
          await this.syncSubscriptionStatus(businessUserId, result.subscription_id);
          
          // Fetch again after sync
          const syncedResponse = await api.get('/subscription/info', {
            params: { business_user_id: businessUserId }
          });
          
          if (syncedResponse.data.status === 'success') {
            return {
              hasSubscription: true,
              ...syncedResponse.data.result
            };
          }
        }

        return {
          hasSubscription: result.has_subscription,
          ...result
        };
      }

      return { hasSubscription: false };
    } catch (error) {
      console.error('[PaymentManagement] Error fetching subscription:', error);
      return { hasSubscription: false };
    }
  }

  /**
   * Sync subscription status with Razorpay
   * 
   * @param {string} businessUserId - The business user ID
   * @param {string} subscriptionId - The Razorpay subscription ID
   */
  async syncSubscriptionStatus(businessUserId, subscriptionId) {
    try {
      await api.post('/subscription/sync', {
        business_user_id: businessUserId,
        subscription_id: subscriptionId
      });
      console.log('[PaymentManagement] Subscription status synced');
    } catch (error) {
      console.error('[PaymentManagement] Error syncing subscription:', error);
    }
  }

  /**
   * Determine payment type and access based on manual plan and subscription
   * 
   * LOGIC:
   * 1. If has active auto-renew subscription (status === 'active') → Use subscription (priority)
   * 2. If subscription is cancelled/expired/created/authenticated → Fall back to manual plan
   * 3. If subscription exists but not active (created, authenticated) → Treat as no subscription
   * 4. If no subscription → Use manual plan
   * 5. If no active payment → Check free trial
   * 6. If trial expired → No access
   * 
   * IMPORTANT: Only status === 'active' means subscription is truly active and paid
   * Other statuses (created, authenticated) mean subscription exists but not yet paid
   * 
   * @param {Object} manualPlan - Manual plan info
   * @param {Object} subscription - Subscription info
   * @param {string} businessUserId - The business user ID
   * @returns {Promise<Object>} Payment determination result
   */
  async determinePaymentType(manualPlan, subscription, businessUserId) {
    // Priority 1: Check Auto-Renew Subscription
    // ⚠️ IMPORTANT: Includes discount subscriptions (first_payment_discount_amount > 0)
    // Active subscription: status === 'active' OR has made first discount payment
    // BUT exclude cancelled subscriptions
    if (subscription.hasSubscription && 
        subscription.status !== 'cancelled' &&
        subscription.status !== 'canceled' &&
        (subscription.status === 'active' || subscription.first_payment_discount_amount > 0)) {
      return {
        paymentType: PAYMENT_TYPE.AUTO_RENEW,
        status: PAYMENT_STATUS.ACTIVE,
        hasAccess: true,
        showManualPayButton: false, // ❌ Hide manual pay button
        showSubscriptionButton: true, // ✅ Show subscription button
        subscriptionInfo: subscription,
        message: subscription.first_payment_discount_amount > 0 
          ? 'Active subscription (first month discounted)' 
          : 'Active auto-renew subscription',
        nextBillingDate: subscription.next_billing_date,
        subscriptionId: subscription.subscription_id,
        paidCount: subscription.paid_count,
        remainingCount: subscription.remaining_count,
        planName: subscription.plan_name // ✅ Add plan name for badge
      };
    }
    
    // Check for other subscription statuses (cancelled, expired, halted, created, authenticated)
    if (subscription.hasSubscription) {
      const subStatus = subscription.status?.toLowerCase();
      
      // Cancelled, created, authenticated, expired, halted - fall back to manual pay
      if (subStatus === 'cancelled' || subStatus === 'canceled' || 
          subStatus === 'created' || subStatus === 'authenticated' ||
          subStatus === 'expired' || subStatus === 'halted') {
        // Check if manual plan is available
        if (manualPlan.hasManualPlan && !manualPlan.is_expired) {
          return {
            paymentType: PAYMENT_TYPE.MANUAL_PAY,
            status: PAYMENT_STATUS.ACTIVE,
            hasAccess: true,
            showManualPayButton: true, // ✅ Show manual pay button
            showSubscriptionButton: true, // ✅ Show subscription button (to reactivate)
            manualPlanInfo: manualPlan,
            subscriptionInfo: subscription,
            message: `Subscription ${subStatus}, using manual plan`,
            expiresOn: manualPlan.expires_on
          };
        }
        
        // No manual plan, subscription not active - treat as no payment
        return {
          paymentType: PAYMENT_TYPE.NONE,
          status: PAYMENT_STATUS.EXPIRED,
          hasAccess: false,
          showManualPayButton: true, // ✅ Show manual pay button
          showSubscriptionButton: true, // ✅ Show subscription button
          subscriptionInfo: subscription,
          message: `Subscription ${subStatus}, no active plan`
        };
      }
    }

    // Priority 2: Check Manual Plan
    if (manualPlan.hasManualPlan) {
      const isExpired = manualPlan.is_expired || manualPlan.plan_status === 'expired';
      
      if (!isExpired && manualPlan.plan_status !== 'none') {
        return {
          paymentType: PAYMENT_TYPE.MANUAL_PAY,
          status: PAYMENT_STATUS.ACTIVE,
          hasAccess: true,
          showManualPayButton: true, // ✅ Show manual pay button (for renewal)
          showSubscriptionButton: true, // ✅ Show subscription button (to upgrade to auto-renew)
          manualPlanInfo: manualPlan,
          message: 'Active manual pay plan',
          expiresOn: manualPlan.expires_on,
          planName: manualPlan.plan_name
        };
      }
      
      // Expired manual plan
      return {
        paymentType: PAYMENT_TYPE.MANUAL_PAY,
        status: PAYMENT_STATUS.EXPIRED,
        hasAccess: false,
        showManualPayButton: true, // ✅ Show manual pay button
        showSubscriptionButton: true, // ✅ Show subscription button
        manualPlanInfo: manualPlan,
        message: 'Manual plan expired',
        expiresOn: manualPlan.expires_on
      };
    }

    // Priority 3: Check Free Trial
    // If no active payment, check if user is in free trial period
    const isInFreeTrial = await this.checkFreeTrial(businessUserId, manualPlan);
    
    if (isInFreeTrial.active) {
      return {
        paymentType: PAYMENT_TYPE.FREE_TRIAL,
        status: PAYMENT_STATUS.ACTIVE,
        hasAccess: true,
        showManualPayButton: true, // ✅ Show manual pay button
        showSubscriptionButton: true, // ✅ Show subscription button
        message: `Free trial active (${isInFreeTrial.daysRemaining} days remaining)`,
        trialDaysRemaining: isInFreeTrial.daysRemaining,
        trialExpiresOn: isInFreeTrial.expiresOn
      };
    }

    // No payment method found and trial expired
    return {
      paymentType: PAYMENT_TYPE.NONE,
      status: PAYMENT_STATUS.EXPIRED,
      hasAccess: false,
      showManualPayButton: true, // ✅ Show manual pay button
      showSubscriptionButton: true, // ✅ Show subscription button
      message: 'No active payment method - trial expired'
    };
  }

  /**
   * Check if user is in free trial period
   * 
   * @param {string} businessUserId - The business user ID
   * @param {Object} manualPlan - Manual plan info
   * @returns {Promise<Object>} Free trial status
   */
  async checkFreeTrial(businessUserId, manualPlan) {
    try {
      // Use planService to check free trial
      const planInfo = await planService.getPlanInfo(businessUserId);
      
      if (planInfo.isFreeTrial) {
        return {
          active: true,
          daysRemaining: planInfo.trialDaysRemaining,
          expiresOn: planInfo.trialExpiresOn
        };
      }
      
      return { active: false };
    } catch (error) {
      console.error('[PaymentManagement] Error checking free trial:', error);
      return { active: false };
    }
  }

  /**
   * Check if user should have site access
   * 
   * @param {string} businessUserId - The business user ID
   * @returns {Promise<boolean>} Whether user has access
   */
  async checkSiteAccess(businessUserId) {
    const paymentInfo = await this.getPaymentInfo(businessUserId);
    return paymentInfo.hasAccess;
  }

  /**
   * Get navigation button visibility
   * 
   * @param {string} businessUserId - The business user ID
   * @returns {Promise<Object>} Button visibility object
   */
  async getNavigationButtonVisibility(businessUserId) {
    const paymentInfo = await this.getPaymentInfo(businessUserId);
    
    return {
      showManualPayButton: paymentInfo.showManualPayButton,
      showSubscriptionButton: paymentInfo.showSubscriptionButton,
      paymentType: paymentInfo.paymentType,
      status: paymentInfo.status
    };
  }
}

// Export singleton instance
const paymentManagementService = new PaymentManagementService();
export default paymentManagementService;
